import { routes } from '@app/constant';
import { ProtectedRoute } from '@app/ProtectedRoute';
import { RoleProtectedRoute } from '@app/RoleProtectedRoute';
import { UserRole } from '@state/userApi';
import { type FC, lazy } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { AnimatedRoutes } from './AnimatedRoutes';
import { LazyRoute } from './LazyRoute';

// Lazy load all page components
const AssignmentPage = lazy(() => import('@pages/Assignment/Assignment'));
const AssignmentCrudPage = lazy(() => import('@pages/AssignmentCrud/AssignmentCrud'));
const AssignmentManagePage = lazy(() => import('@pages/AssignmentManage/AssignmentManage'));
const ChangePasswordPage = lazy(() => import('@pages/ChangePassword/ChangePassword'));
const CourseDetailPage = lazy(() => import('@pages/CourseDetail/CourseDetail'));
const Error404Page = lazy(() => import('@pages/error/404'));
const Error500Page = lazy(() => import('@pages/error/500'));
const HomePage = lazy(() => import('@pages/Home/Home'));
const LearningsPage = lazy(() => import('@pages/Learnings/Learnings'));
const LoginPage = lazy(() => import('@pages/Login/Login'));
const PerfomancePage = lazy(() => import('@pages/Performance/Performance'));
const ResetPage = lazy(() => import('@pages/Reset/Reset'));
const SettingsPage = lazy(() => import('@pages/Settings/Settings'));
const TournamentPage = lazy(() => import('@pages/Tournament/Tournament'));
const TournamentsPage = lazy(() => import('@pages/Tournaments/Tournaments'));
const UserProfilesPage = lazy(() => import('@pages/UserProfiles/UserProfiles'));
const CreateUserPage = lazy(() => import('@pages/CreateUser/CreateUser'));
const EditUserPage = lazy(() => import('@pages/EditUser/EditUser'));
const UserGroupsPage = lazy(() => import('@pages/UserGroups/UserGroups'));
const CreateGroupPage = lazy(() => import('@pages/CreateGroup/CreateGroup'));
const EditGroupPage = lazy(() => import('@pages/EditGroup/EditGroup'));

export const AppRoutes: FC = () => {
    return (
        <AnimatedRoutes>
            <Route path="/" element={<Navigate to={routes.learning} replace />} />
            <Route path={routes.login} element={<LazyRoute component={LoginPage} />} />
            <Route path={routes.reset} element={<LazyRoute component={ResetPage} />} />
            <Route path={routes.changePassword} element={<LazyRoute component={ChangePasswordPage} />} />
            <Route path={routes.error404} element={<LazyRoute component={Error404Page} />} />
            <Route path={routes.error500} element={<LazyRoute component={Error500Page} />} />

            <Route element={<ProtectedRoute />}>
                <Route path={routes.home} element={<LazyRoute component={HomePage} />} />
                <Route path={routes.settings} element={<LazyRoute component={SettingsPage} />} />
                <Route path={routes.learning} element={<LazyRoute component={LearningsPage} />} />
                <Route path={routes.courseDetail} element={<LazyRoute component={CourseDetailPage} />} />

                {/* Routes restricted to non-Driver roles only */}
                <Route
                    element={<RoleProtectedRoute allowedRoles={[UserRole.INSTRUCTOR, UserRole.SYSTEM_ADMINISTRATOR]} />}
                >
                    <Route path={routes.tournaments} element={<LazyRoute component={TournamentsPage} />} />
                    <Route path={routes.tournament} element={<LazyRoute component={TournamentPage} />} />
                    <Route path={routes.assignments} element={<LazyRoute component={AssignmentPage} />} />
                    <Route path={routes.assignment} element={<LazyRoute component={AssignmentCrudPage} />} />
                    <Route path={routes.assignmentDetail} element={<LazyRoute component={AssignmentCrudPage} />} />
                    <Route path={routes.assignmentManage} element={<LazyRoute component={AssignmentManagePage} />} />
                    <Route path={routes.performance} element={<LazyRoute component={PerfomancePage} />} />
                    <Route path={routes.managementUsers} element={<LazyRoute component={UserProfilesPage} />} />
                    <Route path={routes.managementUserCreate} element={<LazyRoute component={CreateUserPage} />} />
                    <Route path={routes.managementUserEdit} element={<LazyRoute component={EditUserPage} />} />
                    <Route path={routes.managementGroups} element={<LazyRoute component={UserGroupsPage} />} />
                    <Route path={routes.managementGroupCreate} element={<LazyRoute component={CreateGroupPage} />} />
                    <Route path={routes.managementGroupEdit} element={<LazyRoute component={EditGroupPage} />} />
                </Route>
            </Route>

            <Route path="*" element={<LazyRoute component={Error404Page} />} />
        </AnimatedRoutes>
    );
};
